import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, FilterSelection, GroupWrapper } from '../Filters.style';
import { FilterOption, FilterResultCounts, RangeResultsCounts } from '../../../types';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import FilterExposedHeader from '../FilterExposedHeader';
import { CommonModelFamiliesValues } from './modelFamiliesConfigs';
import {
  ShowAllButton,
  ModelFamilyStyleFrame,
  AllModelsWrapper,
  Legend,
  CTAWrapper,
  CommonModelsTitle,
} from './ModelFamilySelector.style';
import CommonModelsList from './CommonModels';
import Search from './Search/Search';
import ActiveModelFamiliesList from './ActiveModelFamilies';
import CheckboxSelector from '../../CheckboxComponent';
import useAnimations from '../../../hooks/Animations/useAnimations';

interface ModelFamilySelectorTypes {
  filterType: string;
  modelFamilyOptions: {
    [key: string]: FilterOption;
  };
  temporaryFilters: FilterTypes;
  openedFiltersList: ExposedFiltersTypes;
  handleAddFilter: (filterType: string, value: string) => void;
  handleExposeFilter: (arg: string) => void;
}

const ModelFamilySelector: React.FC<ModelFamilySelectorTypes> = ({
  filterType,
  modelFamilyOptions,
  temporaryFilters,
  handleAddFilter,
  handleExposeFilter,
  openedFiltersList,
}) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  // Animation
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const selectedModelFamilyLabels = useMemo(() => {
    const seletedModelFamilyList = temporaryFilters.modelFamily;
    return seletedModelFamilyList.map((body) => modelFamilyOptions[body]?.label).join(', ');
  }, [modelFamilyOptions, temporaryFilters.modelFamily]);

  const selectedModelFamilyFilters = useMemo(() => {
    return temporaryFilters.modelFamily;
  }, [temporaryFilters.modelFamily]);

  const commmonModelFamilyOptions = useMemo(() => {
    const commonModelFamiliesSet = new Set(CommonModelFamiliesValues);

    const filteredOptions = Object.fromEntries(
      Object.entries(modelFamilyOptions).filter(([key, value]) =>
        commonModelFamiliesSet.has(value.value),
      ),
    );

    // Soriting the object, so it is foloowing initial order from array
    const sortedOptions = Object.fromEntries(
      Object.entries(filteredOptions).sort(([keyA], [keyB]) => {
        return CommonModelFamiliesValues.indexOf(keyA) - CommonModelFamiliesValues.indexOf(keyB);
      }),
    );

    return sortedOptions;
  }, [modelFamilyOptions]);

  const groupedModelFamilies = useMemo(() => {
    // Filter options with defined tabs
    const allOptions = Object.values(modelFamilyOptions);

    // Hide filter options that shouldn't be on the screen
    const filteredOptions = allOptions.filter((option) => !option.isHidden);

    // Group options by tabs using reduce
    const groupedByTabs = filteredOptions.reduce(
      (acc, item) => {
        item.tabs?.forEach((tab) => {
          acc[tab] = acc[tab] || [];
          acc[tab].push(item);
        });
        return acc;
      },
      {} as { [key: string]: FilterOption[] },
    );

    return groupedByTabs;
  }, [modelFamilyOptions]);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [showAll, groupedModelFamilies]);

  const handleAddModelFamily = (newModelFamily: string) => {
    handleAddFilter('modelFamily', newModelFamily);
  };

  const showFilter = openedFiltersList[filterType];

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(showFilter);

  return (
    <Filter>
      <FilterExposedHeader
        filterType={filterType}
        selectedFilterLabels={selectedModelFamilyLabels}
        handleExposeFilter={handleExposeFilter}
        openedFiltersList={openedFiltersList}
      />
      {dropdownAnimation(
        (props, item) =>
          item && (
            <FilterSelection style={props} data-testid="model-family-filter-group">
              <ModelFamilyStyleFrame style={filtersAccordianAnimation}>
                <Search
                  allOptions={Object.keys(modelFamilyOptions)}
                  modelFamilyOptions={modelFamilyOptions}
                  onChange={handleAddModelFamily}
                />
                <ActiveModelFamiliesList
                  modelFamilyOptions={modelFamilyOptions}
                  selectedModelFamilyFilters={selectedModelFamilyFilters}
                  onChange={handleAddModelFamily}
                />
                <CommonModelsTitle>{t(`vlp.filters.models.modelsNote`)}</CommonModelsTitle>
                <CommonModelsList
                  commonModelFamilyOptions={commmonModelFamilyOptions}
                  selectedModelFamilyFilters={selectedModelFamilyFilters}
                  onChange={handleAddModelFamily}
                />
                <CTAWrapper expanded={showAll}>
                  <ShowAllButton
                    onClick={() => setShowAll(!showAll)}
                    data-testid="show-all-filters"
                  >
                    {showAll ? t(`vlp.filters.models.showFewer`) : t(`vlp.filters.models.showAll`)}
                  </ShowAllButton>
                </CTAWrapper>
                <AllModelsWrapper
                  expanded={showAll}
                  ref={contentRef}
                  contentHeight={`${contentHeight}px` || '0'}
                >
                  {Object.keys(groupedModelFamilies).map((group) => {
                    return (
                      <div key={group}>
                        <Legend>{t(`vlp.filters.models.listOfModels.group.${group}`)}</Legend>
                        <GroupWrapper>
                          {groupedModelFamilies[group].map((model) => {
                            return (
                              <CheckboxSelector
                                inputName={`${group}-${model.value}`}
                                key={model.value}
                                onChange={() => handleAddModelFamily(model.value)}
                                value={model.value}
                                label={model.label}
                                ariaLabel={model.ariaLabel}
                                checked={selectedModelFamilyFilters.includes(model.value)}
                                disabled={model.disabled}
                              />
                            );
                          })}
                        </GroupWrapper>
                      </div>
                    );
                  })}
                </AllModelsWrapper>
              </ModelFamilyStyleFrame>
            </FilterSelection>
          ),
      )}
    </Filter>
  );
};

export default ModelFamilySelector;
